/**
 * Venue-local date helpers for the court sheet.
 *
 * The whole sheet reads in the facility timezone. The `date` we send to
 * `getAvailability` must be the venue-local calendar date (YYYY-MM-DD); the
 * backend interprets it in the facility tz. These helpers keep the selected day
 * as a plain calendar date string and resolve its weekday / display label in
 * the venue tz — never the browser tz.
 */

/** Today's calendar date in `timezone`, as YYYY-MM-DD. */
export function venueToday(timezone: string): string {
  return toVenueDateKey(new Date(), timezone);
}

/** A Date's calendar date in `timezone`, as YYYY-MM-DD. */
export function toVenueDateKey(d: Date, timezone: string): string {
  try {
    // en-CA yields YYYY-MM-DD; the timeZone option resolves the venue calendar day.
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
  } catch {
    return new Intl.DateTimeFormat("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
  }
}

/** Step a YYYY-MM-DD key by `days` (calendar-safe via UTC noon to dodge DST). */
export function shiftDateKey(dateKey: string, days: number): string {
  const [y, m, d] = dateKey.split("-").map(Number);
  const base = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  base.setUTCDate(base.getUTCDate() + days);
  const yy = base.getUTCFullYear();
  const mm = String(base.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(base.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

/** Facility-local day of week for a YYYY-MM-DD key (0=Sun..6=Sat). */
export function dayOfWeek(dateKey: string): number {
  const [y, m, d] = dateKey.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12, 0, 0)).getUTCDay();
}

/** Human date label for the nav, e.g. "Mon, Jun 8". */
export function dateLabel(dateKey: string): string {
  const [y, m, d] = dateKey.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(dt);
}

/** Short weekday for a YYYY-MM-DD key, e.g. "Sun" — for a date pill's top line. */
export function dowShort(dateKey: string): string {
  const [y, m, d] = dateKey.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    weekday: "short",
  }).format(dt);
}

/** Day-of-month number for a YYYY-MM-DD key, e.g. "21" — a date pill's big line. */
export function dayNum(dateKey: string): string {
  const [, , d] = dateKey.split("-").map(Number);
  return String(d);
}

/**
 * Shared 12-hour clock label for a whole-hour value, e.g. 9 → "9:00 AM",
 * 20 → "8:00 PM". This is the single source of truth for hour labels on the
 * grid row headers, so every hour (including 20:00/21:00) reads in the same
 * 12-hour format the cart already uses for its time ranges. Formats a fixed
 * UTC instant at the given hour so the output never depends on the viewer tz.
 */
export function hourLabel12(hour: number): string {
  const dt = new Date(Date.UTC(2000, 0, 1, hour % 24, 0, 0));
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(dt);
}
